import { useEffect, useState } from "react";
import { useAtomValue } from "jotai";
import { activeVendorAtom, VendorId } from "./vendor";
import { useMembers, useSelectedMemberKey } from "./daysmart/api";
import {
  useRecusHouseholdMembers,
  useSelectedRecusMemberId,
} from "./recus/api";
import { ThemeSelect } from "./ThemeSelect";
import { Dropdown } from "./components";
import { LoadingDots } from "./LoadingDots";
import { navigate } from ".";

const LOADING_FALLBACK_MS = 5000;

type MemberOption = { id: string; firstName: string };

function useMemberSelection(vendor: VendorId | null): {
  members: MemberOption[] | undefined;
  loading: boolean;
  selectedId: string | null;
  setSelectedId: (id: string) => void;
} {
  const daysmartMembers = useMembers();
  const [daysmartSel, setDaysmartSel] = useSelectedMemberKey();
  const recusMembers = useRecusHouseholdMembers();
  const [recusSel, setRecusSel] = useSelectedRecusMemberId();

  if (vendor === "recus") {
    return {
      members: recusMembers.data?.map((m) => ({
        id: m.id,
        firstName: m.firstName,
      })),
      loading: recusMembers.status !== "success",
      selectedId: recusSel,
      setSelectedId: setRecusSel,
    };
  }
  return {
    members: daysmartMembers.data?.map((m) => ({
      id: m.key,
      firstName: m.firstName,
    })),
    loading: daysmartMembers.status !== "success",
    selectedId: daysmartSel,
    setSelectedId: setDaysmartSel,
  };
}

export function SettingsDropdown() {
  const vendor = useAtomValue(activeVendorAtom);
  const [expanded, setExpanded] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const { members, loading, selectedId, setSelectedId } =
    useMemberSelection(vendor);

  useEffect(() => {
    if (!loading) {
      setTimedOut(false);
      return;
    }
    const t = window.setTimeout(() => setTimedOut(true), LOADING_FALLBACK_MS);
    return () => window.clearTimeout(t);
  }, [loading]);

  if (loading && !timedOut) return <LoadingDots />;

  const currentMember = members?.find((m) => m.id === selectedId);
  const otherMembers = (members ?? []).filter((m) => m.id !== selectedId);
  const label = currentMember
    ? `it's ${currentMember.firstName.toLowerCase()}!`
    : "settings";

  return (
    <>
      <Dropdown
        trigger={
          <button className="flex flex-col items-end">
            <span className="text-lg">{label}</span>
          </button>
        }
        open={expanded}
        onOpenChange={setExpanded}
        className="p-4 whitespace-nowrap text-left bg-bg-brand-alt text-text-on-brand-alt"
      >
        {otherMembers.map((m) => (
          <div key={m.id}>
            <button onClick={() => setSelectedId(m.id)}>
              i'm {m.firstName.toLowerCase()}!
            </button>
          </div>
        ))}
        {otherMembers.length > 0 && (
          <div className="border-t border-border-on-brand-alt my-2" />
        )}
        <div>
          <button onClick={() => navigate("/accounts")}>accounts</button>
        </div>
        <div className="border-t border-border-on-brand-alt my-2" />
        <div>
          <button onClick={() => setShowModal(true)}>theme</button>
        </div>
      </Dropdown>
      {showModal && <ThemeSelect close={() => setShowModal(false)} />}
    </>
  );
}
